import { useEffect, useRef, useState } from 'react';
import { Application, useApplication } from '@pixi/react';
import { Assets, Container, Graphics, Sprite } from 'pixi.js';
import type { CombatResult } from '../game/combatEngine';
import type { Card } from '../game/data/cards/types';
import { CombatUnit } from './CombatUnit';
import { computeStageLayout } from './combatLayout';
import { playEvents, playIntro, type PlaybackContext } from './eventPlayback';
import { backgroundUrlFor } from './backgrounds';
import { CombatCamera } from './camera';
import { attachCameraGestures } from './cameraGestures';
import { useLayoutMode } from '../components/useLayoutMode';

// CombatCanvas — owns the PIXI Application (via @pixi/react) and the
// event-playback loop.
//
// We use @pixi/react's <Application> to host the canvas + lifecycle,
// then drive the scene imperatively from a child component that grabs
// the live PIXI.Application via useApplication(). Per-card sprites are
// CombatUnit class instances with imperative mutators; the playback
// engine drives them directly. We never reach into a sprite from
// outside CombatCanvas — playback talks to CombatUnit's public API.

interface CombatCanvasProps {
  result: CombatResult;
  playerBoard: Card[];
  opponentBoard: Card[];
  speed: 1 | 2 | 4;
  skipSignal: AbortSignal;
  onComplete: () => void;
  /** Match seed for deterministic background selection per match. */
  seed?: number;
  /** Camera glides become cuts, and auto-follow is disabled entirely.
      Continuous camera drift is a genuine vestibular trigger. */
  reducedMotion?: boolean;
}

const BACKGROUND_COLOR = 0x1a1612;

export function CombatCanvas(props: CombatCanvasProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  // The camera outlives the PIXI scene rebuild, so it lives here rather than
  // inside CombatStage — the gesture listeners bind to this host element.
  const cameraRef = useRef<CombatCamera | null>(null);
  const [zoomedIn, setZoomedIn] = useState(false);

  // Desktop AND portrait phones keep the fixed view by default: desktop is
  // already legible, and portrait gets larger tiles from the height-aware
  // fit, so a stable full-board view beats an auto-panning camera. Landscape
  // phones are the one starved case where framing the exchange is the only
  // way to make units readable — they default to the free camera. Either way
  // the player can flip it with the Free View / Fixed View toggle.
  const { mode: layoutMode } = useLayoutMode();
  const [freeView, setFreeView] = useState(layoutMode === 'phoneLandscape');
  useEffect(() => {
    setFreeView(layoutMode === 'phoneLandscape');
  }, [layoutMode]);

  useEffect(() => {
    if (!hostRef.current) return;
    const el = hostRef.current;
    const update = () => {
      const w = Math.min(1200, el.clientWidth || 360);
      // Lower floor (200) so landscape mobile (≤393px tall) can fit the
      // canvas without forcing it taller than the viewport.
      const h = Math.max(200, el.clientHeight || 280);
      setSize({ w, h });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Create/resize the camera whenever the canvas size settles. The world is
  // exactly canvas-sized (the scene is laid out to fit at zoom 1), so world
  // and view dimensions match — zoom 1 means "everything visible".
  useEffect(() => {
    if (!size) return;
    const view = { worldW: size.w, worldH: size.h, viewW: size.w, viewH: size.h };
    if (!cameraRef.current) cameraRef.current = new CombatCamera(view);
    else cameraRef.current.setViewport(view);
    cameraRef.current.reducedMotion = props.reducedMotion === true;
    cameraRef.current.followSuppressed = props.reducedMotion === true;
    cameraRef.current.setLocked(!freeView);
  }, [size, props.reducedMotion, freeView]);

  // Gestures bind to the host, not to PIXI — see cameraGestures.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !size) return;
    const camera = cameraRef.current;
    if (!camera) return;
    const detach = attachCameraGestures(host, camera);
    // Poll the zoom flag for the overview button's visibility. Cheap, and it
    // avoids threading a subscription through the camera for one boolean.
    const id = window.setInterval(() => setZoomedIn(camera.isZoomedIn), 200);
    return () => {
      detach();
      window.clearInterval(id);
    };
  }, [size]);

  return (
    <div
      ref={hostRef}
      className="relative w-full flex-1 min-h-0 overflow-hidden rounded-md bg-asphalt"
      style={{ touchAction: 'none' }}
    >
      {size ? (
        <Application
          width={size.w}
          height={size.h}
          backgroundColor={BACKGROUND_COLOR}
          antialias
          autoDensity
          resolution={window.devicePixelRatio || 1}
        >
          <CombatStage
            {...props}
            width={size.w}
            height={size.h}
            cameraRef={cameraRef}
          />
        </Application>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-data text-[12px] uppercase tracking-[0.3em] text-marker">
            preparing combat…
          </span>
        </div>
      )}

      {/* Camera controls, top-right.
          - The Free/Fixed toggle is always available, so desktop can opt in
            to the camera and phones can opt out to a stable view.
          - Recenter only appears while free AND zoomed in: it's the one way
            back to auto-follow after taking manual control, and it would be
            meaningless in either other state. */}
      {size && (
        <div className="absolute right-[8px] top-[8px] z-10 flex items-center gap-xs">
          {freeView && zoomedIn && (
            <button
              onClick={() => {
                cameraRef.current?.reset({ resumeFollow: true });
                setZoomedIn(false);
              }}
              title="Back to the whole battlefield and resume auto-follow"
              className="rounded-sm border border-sodium/40 bg-stucco1/90 px-sm py-xs font-data text-[10px] uppercase tracking-[0.2em] text-sodium hover:bg-stucco2"
            >
              ⤢ recenter
            </button>
          )}
          <button
            onClick={() => setFreeView((v) => !v)}
            aria-pressed={freeView}
            title={
              freeView
                ? 'Lock the camera to the full battlefield'
                : 'Pan, zoom, and follow the action'
            }
            className={`rounded-sm border px-sm py-xs font-data text-[10px] uppercase tracking-[0.2em] hover:bg-stucco2 ${
              freeView
                ? 'border-sodium/40 bg-sodium/15 text-sodium'
                : 'border-stucco3 bg-stucco1/90 text-graphite'
            }`}
          >
            {freeView ? '⊟ fixed view' : '⊞ free view'}
          </button>
        </div>
      )}
    </div>
  );
}

// Inner stage component — runs INSIDE the <Application> context so it can
// pull the live PIXI.Application via useApplication(). We build the entire
// scene imperatively here on mount and rely on a stable mount per combat
// (the parent rekeys on round number).
function CombatStage({
  result,
  playerBoard,
  opponentBoard,
  speed,
  skipSignal,
  onComplete,
  seed,
  width,
  height,
  cameraRef,
}: CombatCanvasProps & {
  width: number;
  height: number;
  cameraRef: React.MutableRefObject<CombatCamera | null>;
}) {
  const { app, isInitialised } = useApplication();
  const speedRef = useRef(speed);
  const completedRef = useRef(false);

  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  // Pause the Pixi ticker while the tab/app is backgrounded. Prevents the
  // render loop from burning battery on cheap Android during a long match.
  useEffect(() => {
    if (!isInitialised || !app) return;
    const handleVisibility = () => {
      if (document.hidden) app.ticker.stop();
      else app.ticker.start();
    };
    if (document.hidden) app.ticker.stop();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isInitialised, app]);

  useEffect(() => {
    if (!isInitialised || !app) return;

    // --- Stage layers ---
    // `cameraLayer` exists solely so the camera has a transform of its own.
    // `shakeStage()` in eventPlayback writes stage.x/y and resets them to 0,
    // so the camera cannot share that node — it would be snapped back to the
    // origin by every screen shake.
    const cameraLayer = new Container();
    app.stage.addChild(cameraLayer);

    const stage = new Container();
    cameraLayer.addChild(stage);

    // Background layer — behind everything, holds the optional scene image.
    // Loaded async; sprite appears once the texture resolves. The dark
    // overlay keeps unit silhouettes legible against any background.
    const bgLayer = new Container();
    bgLayer.eventMode = 'none';
    stage.addChild(bgLayer);

    const bgDim = new Graphics();
    bgDim.rect(0, 0, width, height);
    bgDim.fill({ color: 0x1a1612, alpha: 0.55 });
    bgDim.alpha = 0; // hidden until bg image loads
    bgDim.eventMode = 'none';
    bgLayer.addChild(bgDim);

    if (seed !== undefined) {
      const bgUrl = backgroundUrlFor(seed);
      Assets.load(bgUrl)
        .then((tex) => {
          if (cancelled) return;
          const sprite = new Sprite(tex);
          // Fit cover — fill the canvas while preserving aspect.
          const texW = sprite.width;
          const texH = sprite.height;
          const scale = Math.max(width / texW, height / texH);
          sprite.scale.set(scale);
          sprite.x = (width - texW * scale) / 2;
          sprite.y = (height - texH * scale) / 2;
          sprite.alpha = 0.35;
          sprite.eventMode = 'none';
          bgLayer.addChildAt(sprite, 0);
          bgDim.alpha = 1;
        })
        .catch(() => {
          // Background is decorative — silently fall through if it fails.
        });
    }

    const units = new Container();
    stage.addChild(units);

    const fx = new Container();
    fx.eventMode = 'none';
    stage.addChild(fx);

    // Flash stays a direct child of app.stage — outside the camera — so a
    // full-screen hit flash covers the screen regardless of zoom or pan.
    const flash = new Graphics();
    flash.rect(0, 0, width, height);
    flash.fill({ color: 0xffffff });
    flash.alpha = 0;
    flash.eventMode = 'none';
    app.stage.addChild(flash);

    // Drive the camera off the PIXI ticker so easing is frame-rate correct
    // and stops automatically when the ticker is paused (backgrounded tab).
    const tickCamera = (ticker: { deltaTime: number }) => {
      cameraRef.current?.update(cameraLayer, ticker.deltaTime);
    };
    app.ticker.add(tickCamera);

    // --- Build initial unit sprites ---
    const playerSprites: CombatUnit[] = [];
    const enemySprites: CombatUnit[] = [];
    const byId = new Map<string, CombatUnit>();

    // Tile size + row placement — see combatLayout.ts (pure, unit-tested).
    const playerCount = (playerBoard.filter(Boolean) as Card[]).length;
    const enemyCount = opponentBoard.length;
    const { unifiedScale, tileW, tileH, gap, enemyTopY, playerTopY } =
      computeStageLayout(width, height, playerCount, enemyCount);

    const computeHomes = (count: number, rowY: number) => {
      if (count === 0) return [];
      const totalW = count * tileW + (count - 1) * gap;
      const startX = (width - totalW) / 2 + tileW / 2;
      const homes: Array<{ x: number; y: number; scale: number }> = [];
      for (let i = 0; i < count; i++) {
        homes.push({
          x: startX + i * (tileW + gap),
          y: rowY + tileH / 2,
          scale: unifiedScale,
        });
      }
      return homes;
    };

    function relayout(owner: 'player' | 'enemy') {
      const list = owner === 'player' ? playerSprites : enemySprites;
      const live = list.filter((u) => !u.isDead());
      const homes = computeHomes(
        live.length,
        owner === 'player' ? playerTopY : enemyTopY,
      );
      live.forEach((unit, i) => {
        const h = homes[i];
        unit.homeX = h.x;
        unit.homeY = h.y;
        unit.x = h.x;
        unit.y = h.y;
        unit.scale.set(h.scale);
      });
    }

    const playerCards = playerBoard.filter(Boolean) as Card[];
    const enemyCards = opponentBoard;

    const enemyHomes = computeHomes(enemyCards.length, enemyTopY);
    enemyCards.forEach((card, i) => {
      const home = enemyHomes[i];
      const unit = new CombatUnit(card, 'enemy', home.x, home.y);
      unit.scale.set(home.scale);
      units.addChild(unit);
      enemySprites.push(unit);
      if (card.instanceId) byId.set(card.instanceId, unit);
    });

    const playerHomes = computeHomes(playerCards.length, playerTopY);
    playerCards.forEach((card, i) => {
      const home = playerHomes[i];
      const unit = new CombatUnit(card, 'player', home.x, home.y);
      unit.scale.set(home.scale);
      units.addChild(unit);
      playerSprites.push(unit);
      if (card.instanceId) byId.set(card.instanceId, unit);
    });

    // --- PlaybackContext ---
    const ctx: PlaybackContext = {
      layers: { stage, units, fx, flash },
      getUnit: (id) => byId.get(id),
      addUnit: (unit, owner, position) => {
        units.addChild(unit);
        if (owner === 'player') {
          playerSprites.splice(position, 0, unit);
        } else {
          enemySprites.splice(position, 0, unit);
        }
        if (unit.card.instanceId) byId.set(unit.card.instanceId, unit);
        relayout(owner);
      },
      relayout,
      getSpeed: () => speedRef.current,
      signal: skipSignal,
      width,
      height,
      focusCamera: (points) => {
        const camera = cameraRef.current;
        if (!camera) return;
        // At 4x the exchanges land faster than the camera can settle, so
        // following would just smear. Park at fit and let the player watch
        // the whole board. Read through the ref so changing speed mid-combat
        // takes effect immediately.
        if (speedRef.current >= 4) {
          camera.reset();
          return;
        }
        // `focusOn` is itself a no-op while the player has manual control or
        // reduced motion is on — those flags are owned by the parent, so this
        // closure never has to read a possibly-stale prop.
        camera.focusOn(points);
      },
    };

    let cancelled = false;
    // Intro flourish ("FIGHT" punch-in + sodium ring) then the actual
    // event stream. Both honor the skipSignal — hitting Skip during intro
    // drops directly into events.
    (async () => {
      try {
        await playIntro(ctx);
        await playEvents(result, ctx);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[combat] playback error:', err);
      } finally {
        if (cancelled || completedRef.current) return;
        completedRef.current = true;
        onComplete();
      }
    })();

    return () => {
      cancelled = true;
      // Clean our containers off the app stage. @pixi/react owns the
      // Application itself; we own only what we added.
      try {
        app.ticker.remove(tickCamera);
        if (cameraLayer.parent) cameraLayer.parent.removeChild(cameraLayer);
        cameraLayer.destroy({ children: true });
        if (flash.parent) flash.parent.removeChild(flash);
        flash.destroy();
      } catch {
        // already destroyed
      }
    };
    // Mount-once: this stage runs against the snapshot props captured by
    // the parent. Parent rekeys when round changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialised, app]);

  return null;
}
